import React, { useState } from 'react';
import { userPictureUrl } from '@/lib/media';
import ModelCardPlaceholder from './ModelCardPlaceholder';


function PostCard({ post, onClick, onLike, getMediaUrl, onOpenProfile }) {
  const [liked, setLiked] = useState(post.is_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count);
  const [loaded, setLoaded] = useState(post.media_type === '3d');
  const [sensitiveRevealed, setSensitiveRevealed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLike = async (e) => {
    e.stopPropagation();
    const newLiked = !liked;
    setLiked(newLiked);
    setLikesCount(prev => newLiked ? prev + 1 : prev - 1);
    try {
      await onLike(post.post_id);
    } catch (err) {
      // revert on error
      setLiked(!newLiked);
      setLikesCount(prev => newLiked ? prev - 1 : prev + 1);
    }
  };

  const sharePost = async (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    const url = `${window.location.origin}/?post=${encodeURIComponent(post.post_id)}`;
    try {
      if (navigator.share) await navigator.share({ title: post.title, url });
      else {
        await navigator.clipboard.writeText(url);
        window.dispatchEvent(new CustomEvent('blop:notice', { detail: 'Link copiado.' }));
      }
    } catch (_) { /* compartilhamento cancelado */ }
  };

  return (
    <div
      className="masonry-item relative rounded-2xl overflow-hidden cursor-pointer flex flex-col group transition-all hover:-translate-y-1"
      onClick={() => onClick(post)}
      data-testid={`post-card-${post.post_id}`}
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--glass-border)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)';
        e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--glass-border)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* User header */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onOpenProfile?.(post.user?.user_id); }}
        className="post-author-header flex items-center gap-2 px-3 py-2.5 z-[2]"
        style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
      >
        {post.user?.picture ? (
          <img src={userPictureUrl(post.user.picture, getMediaUrl)} alt="" className="w-6 h-6 rounded-full object-cover" />
        ) : (
          <i className="ti ti-user text-[14px] text-white/40"></i>
        )}
        <span className="text-[11px] font-semibold text-white/85 truncate">{post.user?.name || 'Anônimo'}</span>
        <span 
          className="ml-auto text-[9px] uppercase font-bold px-1.5 py-0.5 rounded"
          style={{ color: 'var(--accent)' }}
        >
          BlopBoru
        </span>
      </button>

      {/* Image wrapper */}
      <div className="relative w-full overflow-hidden" style={{ WebkitTouchCallout: 'none' }}>
        {!loaded && (
          <div className="w-full h-52 flex items-center justify-center">
            <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{borderColor:'var(--accent)', borderTopColor:'transparent'}}></div>
          </div>
        )}
        {post.media_type === '3d' ? (
          <ModelCardPlaceholder title={post.title} thumbnail={post.thumbnail_url&&post.thumbnail_url!==post.media_url?getMediaUrl(post.thumbnail_url):''} />
        ) : post.media_type === 'video' ? (
          <video
            src={getMediaUrl(post.media_url)}
            className={`w-full block object-cover ${!loaded ? 'hidden' : ''} ${post.is_nsfw && !sensitiveRevealed ? 'sensitive-media-blur' : ''}`}
            onLoadedData={() => setLoaded(true)}
            onCanPlay={(event) => {
              setLoaded(true);
              event.currentTarget.play().catch(() => {});
            }}
            onError={() => setLoaded(true)}
            muted
            autoPlay
            loop
            playsInline
            preload="metadata"
          />
        ) : (
          <img
            src={getMediaUrl(post.media_url)}
            alt={post.title}
            className={`w-full block object-cover transition-transform ${!loaded ? 'hidden' : ''} ${post.is_nsfw && !sensitiveRevealed ? 'sensitive-media-blur' : ''}`}
            onLoad={() => setLoaded(true)}
          />
        )}

        {post.is_nsfw && (
          <div className="absolute top-2.5 right-2.5 z-[3] rounded-md px-1.5 py-0.5 text-[10px] font-bold text-white bg-red-500">
            +18
          </div>
        )}
        {post.is_nsfw && !sensitiveRevealed && loaded && (
          <button type="button" className="sensitive-media-cover" onClick={(e) => { e.stopPropagation(); setSensitiveRevealed(true); }}>
            <i className="ti ti-eye-off" /><strong>Conteúdo sensível</strong><span>Toque para visualizar</span>
          </button>
        )}

        {/* Overlay */}
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
          style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 60%)' }}
        />

        <div className="post-mobile-signals" aria-label="Informações da publicação">
          <button type="button" className="post-signal-views" onClick={e=>e.stopPropagation()} aria-label={`${post.views_count || 0} visualizações`}><i className="ti ti-eye"/><span>{post.views_count || 0}</span></button>
          <button type="button" className={liked?'post-signal-like liked':'post-signal-like'} onClick={handleLike} aria-label={liked?'Descurtir':'Curtir'}><i className={liked?'ti ti-heart-filled':'ti ti-heart'}/><span>{likesCount || 0}</span></button>
          <button type="button" className="post-signal-more" onClick={e=>{e.stopPropagation();setMenuOpen(value=>!value);}} aria-label="Mais opções"><i className="ti ti-dots"/></button>
        </div>

        {/* Actions */}
        <div className="post-actions absolute bottom-0 left-0 right-0 flex items-center justify-between p-3 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all z-[2] pointer-events-none group-hover:pointer-events-auto">
          <span className="text-xs font-semibold text-white truncate max-w-[55%]">{post.title}</span>
          <div className="post-card-menu-wrap">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setMenuOpen(value => !value); }}
              className="post-more-button"
              aria-label="Mais opções"
              aria-expanded={menuOpen}
            >
              <i className="ti ti-dots"></i>
            </button>
            {menuOpen && <>
              <button type="button" className="post-card-menu-backdrop" aria-label="Fechar menu" onClick={(e)=>{e.stopPropagation();setMenuOpen(false);}} />
              <div className="post-card-menu" role="menu" onClick={e=>e.stopPropagation()}>
                <button type="button" role="menuitem" onClick={async e=>{await handleLike(e);setMenuOpen(false);}} data-testid={`like-btn-${post.post_id}`} className={liked?'liked':''}><i className={liked?'ti ti-heart-filled':'ti ti-heart'}/><span>{liked?'Descurtir':'Curtir'}{likesCount>0&&<small>{likesCount}</small>}</span></button>
                <button type="button" role="menuitem" onClick={(e)=>{e.stopPropagation();setMenuOpen(false);onClick(post);}}><i className="ti ti-maximize"/><span>Abrir obra</span></button>
                <button type="button" role="menuitem" onClick={(e)=>{e.stopPropagation();setMenuOpen(false);onOpenProfile?.(post.user?.user_id);}}><i className="ti ti-user"/><span>Ver artista</span></button>
                <button type="button" role="menuitem" onClick={sharePost}><i className="ti ti-share-3"/><span>Compartilhar</span></button>
              </div>
            </>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PostCard;
